import {useEffect, useState, useRef} from "react";
import State from "./State";
import { useToasts } from "../Components/Toasts";
import API from "../Services/API";
import { useSocketListener } from "../Components/Socket";
import Callback from "../Services/Callback";
import DB from "./DB";
import { toQueryString, val } from "../Services/helpers";
import { useOffline } from "../Components/Offline";

const promises = {};
const modelCallback = new Callback();

const isEmpty = (model, defaultData) => {
    if (model === null || model === undefined) {
        return true;
    }

    if (JSON.stringify(model) === JSON.stringify(defaultData)) {
        return true;
    }

    return false;
}

const Model = (props) => {
    const {
        fetchData, returnDataKey, stateKey, defaultData, saveAdditionalData,
        block, alwaysRefresh, defaultLoadingError, registerAdditionalMethods,
        subscribeToUpdates, suppressLoadingError, persist, abortable,
    } = props;

    const {isOnline, removeQueuedTask} = useOffline();
    const {showError, showSuccess} = useToasts();

    const _stateKey = makeStateKey(stateKey);
    const abortControllerRef = useRef(null);

    const [model, setModel] = useState(defaultData ?? State.get(_stateKey, null));
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasLoaded, setHasLoaded] = useState({});
    const [offlineUnavailable, setOfflineUnavailable] = useState(false);

    const doFetchData = async (force = false) => {
        if (block === true) {
            return;
        }

        if (!isEmpty(State.get(_stateKey), defaultData) && force !== true) {
            setModel(State.get(_stateKey));
            setOfflineUnavailable(false);

            if ((hasLoaded[_stateKey] === true || alwaysRefresh !== true) && force === false) {
                setIsLoading(false);
                return;
            }
        }

        if (!isOnline && !promises[_stateKey]) {
            setOfflineUnavailable(isEmpty(model, defaultData));
            setIsLoading(false);
            return;
        }

        let abortController = null;

        if (abortable) {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            abortController = new AbortController();
            abortControllerRef.current = abortController;
        }

        setOfflineUnavailable(false);
        setHasLoaded({...hasLoaded, [_stateKey]: true});
        setIsLoading(true);

        const response = await handleFetchData(
            _stateKey,
            fetchData,
            showError,
            setIsLoading,
            defaultLoadingError,
            suppressLoadingError || !isOnline,
            abortController?.signal ?? null,
        );

        delete promises[_stateKey];

        State.put(_stateKey, response);
        modelCallback.invoke(_stateKey, response);

        if (DB.ready && val(persist)) {
            DB.set(_stateKey, response);
        }

        if (saveAdditionalData) {
            saveAdditionalData(response);
        }

        setModel(response);
        setIsLoading(false);

        return response;
    }

    useEffect(() => {
        doFetchData(false);

        return () => {
            if (abortable && abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_stateKey, abortable]);

    // This can fire before isOnline is updated, so just watch for isOnline
    // useBackOnline(() => {
    useEffect(() => {
        if (isOnline && (alwaysRefresh || model === null)) {
            doFetchData(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alwaysRefresh, isOnline]);

    // If the key is updated anywhere else, update the model
    useEffect(() => {
        return modelCallback.listen((k, v) => {
            if (k === _stateKey) {
                setModel(v);
            }
        });
    }, [_stateKey]);

    const callbackOptions = {
        model: () => model,
        updateModel: (newModel) => {
            State.put(_stateKey, newModel);
            setModel(newModel);
        },
        refresh: () => doFetchData(true),
        setIsSaving,
        showError,
        showSuccess,
        wrapUpdateCall: (fn, successMsg, errorMsg, refreshAfterCall = true, removeFromQueue = null) => {
            setIsSaving(true);
            return fn().then((response) => {
                setIsSaving(false);
                refreshAfterCall && doFetchData(true);
                successMsg && showSuccess(successMsg);
                if (removeFromQueue) {
                    removeQueuedTask(removeFromQueue.task, removeFromQueue.entity);
                }
                return response;
            }).catch((err) => {
                setIsSaving(false);
                if (errorMsg === true) {
                    showError(API.parseErrorResponse(err, defaultLoadingError ?? 'Sorry, something went wrong.'));
                } else if (errorMsg) {
                    showError(errorMsg);
                }
                throw err;
            });
        }
    };

    useSocketListener(
        subscribeToUpdates ? (subscribeToUpdates?.entity ? {entity: subscribeToUpdates?.entity} : {topic: subscribeToUpdates?.topic ?? null}) : null,
        subscribeToUpdates?.handler ? (data) => subscribeToUpdates.handler(data, callbackOptions) : callbackOptions.updateModel,
    );

    let additionalMethods = {};
    if (registerAdditionalMethods) {
        additionalMethods = registerAdditionalMethods(callbackOptions);
    }

    const returnData = {
        refresh: () => doFetchData(true),
        isLoading: (isLoading || promises[_stateKey] || model === null) ?? false,
        isEmpty: (Array.isArray(model) && model.length === 0),
        ...additionalMethods,
        isSaving,
        offlineUnavailable,
        doWait: async () => {
            return promises[_stateKey] ?? null;
        }
    };
    returnData[returnDataKey] = model;

    return returnData;
}


const handleFetchData = async (_stateKey, fetchData, showError, setIsLoading, defaultLoadingError, suppressLoadingError = false, signal = null) => {
    if (promises[_stateKey]) {
        return promises[_stateKey];
    }

    return promises[_stateKey] = fetchData(signal).catch(err => {
        delete promises[_stateKey];
        setIsLoading(false);

        if (signal?.aborted || (err?.name === 'AbortError' || err?.message?.includes('aborted'))) {
            return null;
        }

        if (suppressLoadingError === true) {
            return null;
        }

        if (err && err?.[0]) {
            showError(err[0]?.title ?? defaultLoadingError, err[0]?.detail);
        } else {
            showError(API.parseErrorResponse(err, defaultLoadingError ?? 'Sorry, something went wrong.'));
        }

        throw err;
    });
}

const makeStateKey = (stateKey) => {
    if (Array.isArray(stateKey)) {
        return stateKey.map(k => makeStateKey(k)).join('-');
    }

    if (typeof stateKey === 'object') {
        return toQueryString(stateKey);
    }

    return stateKey;
}

export default Model;

export {
    modelCallback,
    makeStateKey,
}
